/**
 * PATH: packages/ui/src/components/mobile-dock/MobileDockCollapseControls.tsx
 * FILE_ID: MASTER-MOBILEDOCK-COLLAPSE-CONTROLS-001
 * PURPOSE: Render the MobileDock collapse and restore affordances.
 * WHY: Keeps dock orchestration below the file-size ceiling.
 * FLOW: collapsed state -> restore pill / collapse button -> parent callback.
 * DEPENDENCIES: lucide-react, className helper.
 * RELATED: index.tsx, useMobileDockScroll.ts, mobileDockChrome.ts.
 */

import { ChevronDown, ChevronRight } from 'lucide-react'

import { cn } from '../../lib/utils'
import { useEdgeSnapCornerDrag, type SnapCorner } from '@quotech/ui/mobile'
import { MOBILE_DOCK_SHELL } from './mobileDockChrome'

const RESTORE_CORNER_KEY = 'quotech.master-mobile-dock.restore.corner'

/** Owned by the parent (MobileDock) so the collapsed `--mobile-dock-h`
 *  reserve can be corner-aware: a pill parked at a TOP corner occupies no
 *  floor space, so pages should not keep a dead band under their composers. */
export function useRestoreCornerDrag() {
  return useEdgeSnapCornerDrag(RESTORE_CORNER_KEY, 'bl')
}

function restoreCornerClass(corner: SnapCorner): string {
  if (corner === 'tl') return 'left-2 top-0 bottom-auto right-auto'
  if (corner === 'tr') return 'right-2 top-0 bottom-auto left-auto'
  if (corner === 'br') return 'right-2 bottom-0 left-auto top-auto'
  return 'left-2 bottom-0 right-auto top-auto'
}

export function MobileDockRestoreControl({
  collapsed,
  onRestore,
  drag,
  compact = false,
}: {
  collapsed: boolean
  onRestore: () => void
  drag: ReturnType<typeof useRestoreCornerDrag>
  /** A page asked for the floor: a 36px round 4-dot button instead of "Menu >". */
  compact?: boolean
}) {
  const { onPointerUp, ...dragBind } = drag.bind
  return (
    <div
      className={cn(
        // Above the collapsed dock strip. Same z-index let that strip
        // take the tap even while it was slid off the floor.
        'fixed z-40 transition-all duration-300 ease-out',
        restoreCornerClass(drag.corner),
        collapsed
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 translate-y-3 pointer-events-none',
      )}
      style={{ marginBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
      data-master-mobile-dock-restore="true"
      data-corner={drag.corner}
      data-compact={compact ? 'true' : undefined}
      {...dragBind}
      onPointerUp={(event) => {
        const wasDrag = drag.dragged.current
        onPointerUp(event)
        if (!wasDrag) onRestore()
      }}
    >
      <button
        type="button"
        onClick={() => {
          if (drag.dragged.current) {
            drag.dragged.current = false
            return
          }
          onRestore()
        }}
        aria-label="Show navigation"
        aria-expanded={false}
        className={cn(
          compact
            ? 'flex h-9 w-9 items-center justify-center rounded-full touch-none'
            : 'flex items-center gap-1.5 rounded-full px-3 py-2 touch-none',
          MOBILE_DOCK_SHELL,
          'text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors',
        )}
      >
        {compact ? (
          <span className="grid grid-cols-2 gap-0.5" aria-hidden>
            {[0, 1, 2, 3].map((i) => <span key={i} className="h-1 w-1 rounded-full bg-current" />)}
          </span>
        ) : (
          <>
            <span className="text-xs font-semibold">Menu</span>
            <ChevronRight className="h-4 w-4" />
          </>
        )}
      </button>
    </div>
  )
}

export function MobileDockCollapseControl({ onCollapse }: { onCollapse: () => void }) {
  return (
    <button
      type="button"
      onClick={onCollapse}
      aria-label="Hide navigation"
      className={cn(
        'flex-shrink-0 flex items-center justify-center',
        'w-8 my-1.5 ml-1 rounded-lg',
        'text-muted-foreground hover:text-foreground',
        'hover:bg-accent transition-colors',
        'border-r border-border/70',
      )}
    >
      <ChevronDown className="h-4 w-4" />
    </button>
  )
}
